import strings from '@shared/strings.da.json';

/**
 * Every user-facing string comes from shared/strings.da.json (CLAUDE.md, rule 5). No Danish
 * text is written in a component: a missing string is added to that file and used by key.
 *
 * The key type is derived from the file itself, so a typo or a removed key is a build error
 * rather than a blank space on a trade-show stand.
 */
type Leaves<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${Prefix}${K}`
    : T[K] extends Record<string, unknown>
      ? Leaves<T[K], `${Prefix}${K}.`>
      : never;
}[keyof T & string];

export type StringKey = Leaves<typeof strings>;

type Placeholders = Readonly<Record<string, string | number>>;

function lookup(key: string): string {
  let current: unknown = strings;

  for (const part of key.split('.')) {
    if (typeof current !== 'object' || current === null) {
      current = undefined;
      break;
    }
    current = (current as Record<string, unknown>)[part];
  }

  if (typeof current !== 'string') {
    // Loud in development, harmless in production: a stand must never show a raw key, but a
    // developer must never miss one either.
    if (import.meta.env.DEV) {
      throw new Error(`The string "${key}" is missing from shared/strings.da.json.`);
    }
    return '';
  }

  return current;
}

/** Looks up a string and substitutes placeholders written {likeThis}. */
export function t(key: StringKey, values?: Placeholders): string {
  const text = lookup(key);
  if (!values) {
    return text;
  }

  return Object.entries(values).reduce(
    (result, [name, value]) => result.split(`{${name}}`).join(String(value)),
    text,
  );
}

export const consentTextVersion: string = strings.samtykke.version;

export const stringsVersion: string = strings.version;

/**
 * Maps API enum values onto the Danish string keys. The API speaks English and the strings
 * file is Danish content; this is the single place the two meet.
 */
export const mailStatusKey = {
  queued: 'admin.mailStatus.iKoe',
  sent: 'admin.mailStatus.sendt',
  failed: 'admin.mailStatus.fejlet',
} as const satisfies Record<string, StringKey>;

export const residualReasonKey = {
  supplyChain: 'anbefaling.aarsag.forsyningskaede',
  frontendLockIn: 'anbefaling.aarsag.frontendbinding',
  exitAndResilience: 'anbefaling.aarsag.exitOgResiliens',
  strategicOwnership: 'anbefaling.aarsag.strategiskEjerskab',
  other: 'anbefaling.aarsag.andet',
} as const satisfies Record<string, StringKey>;

export const organisationTypeKey = {
  private: 'opsaetning.typePrivat',
  public: 'opsaetning.typeOffentlig',
} as const satisfies Record<string, StringKey>;

export const channelKey = {
  salesRep: 'admin.kilde.salesRep',
  selfService: 'admin.kilde.selfService',
} as const satisfies Record<string, StringKey>;

/**
 * `typicalEffort` on a Candidate is free text from offerings.draft.json ("lav"/"moderat"/"hoej"),
 * not a typed API enum — RecommendationEngine.cs matches those same raw strings case-insensitively
 * to sort candidates, so they cannot be changed there without touching that logic too. This maps
 * them to a properly spelled, properly cased Danish label for display only.
 */
export const typicalEffortKey = {
  lav: 'anbefaling.indsatsLav',
  moderat: 'anbefaling.indsatsModerat',
  hoej: 'anbefaling.indsatsHoej',
} as const satisfies Record<string, StringKey>;
